import { useEffect, useRef, useState } from 'react';

interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number;
  disabled?: boolean;
}

export function usePullToRefresh({ onRefresh, threshold = 80, disabled = false }: UsePullToRefreshOptions) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (disabled) return;

    const handleTouchStart = (e: TouchEvent) => {
      const target =
        e.target instanceof Element
          ? e.target
          : e.target instanceof Node
            ? e.target.parentElement
            : null;
      // Horizontal carousels and tappable cards must keep their own gestures.
      if (target?.closest('[data-h-scroll], [data-promo-card], a, button, [role="link"]')) {
        startY.current = 0;
        return;
      }
      // Only allow pull-to-refresh when at the top of the page
      if (window.scrollY === 0 || (containerRef.current && containerRef.current.scrollTop === 0)) {
        startY.current = e.touches[0].clientY;
        currentY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (startY.current === 0) return;
      
      // Only allow pull-to-refresh when at the top
      if (window.scrollY > 0 && (!containerRef.current || containerRef.current.scrollTop > 0)) {
        startY.current = 0;
        setPullDistance(0);
        return;
      }

      currentY.current = e.touches[0].clientY;
      const distance = Math.max(0, currentY.current - startY.current);
      // Tiny movement is a tap. preventDefault here cancels clicks on Android WebView.
      if (distance > 16) {
        e.preventDefault();
        setPullDistance(distance);
      }
    };

    const handleTouchEnd = async () => {
      if (startY.current === 0 || isRefreshing) {
        startY.current = 0;
        currentY.current = 0;
        setPullDistance(0);
        return;
      }

      if (pullDistance >= threshold) {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
      
      startY.current = 0;
      currentY.current = 0;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh, threshold, disabled, pullDistance, isRefreshing]);

  return {
    isRefreshing,
    pullDistance,
    pullProgress: Math.min(pullDistance / threshold, 1),
    containerRef
  };
}
